/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import throttle from 'lodash/throttle';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import {
    FlexRow,
    FlexCol,
    SearchActions as GaActions,
    withLDConsumer,
    StyledModal,
    Text,
    ModalActions,
    Button,
} from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';

import * as actions from '../actions/search';
import SearchResultsList from './SearchResultsList';
import SearchCriteriaInputs from './SearchCriteriaInputs';
import * as types from '../constants';
// import TripPreferencesModal from '../components/tripPreferences/TripPreferencesModal';

import { SearchActionBarOverlay } from './SearchActionBarOverlay';
import { showFullScreenMap } from '../utils/mapUtils';
import AiSearchInput from '../components/aiSearch/AiSearchInput';
import { processQueryParams } from '../utils/filters';

import { SearchResultsMapV2 } from '../components/SearchResultsMapV2/SearchResultsMapV2';
import { SearchResultsMapV2Modal } from '../components/SearchResultsMapV2/SearchResultsMapV2Modal';
import { MapControlProvider } from '../context/MapControlContext';

const MOBILE_WIDTH = 992;

class SearchResultsPage extends React.Component {
    static propTypes = {
        dispatch: PropTypes.func.isRequired,
        router: COMPAT_ROUTER_PROP_TYPES,
        searchResults: PropTypes.object,
        search: PropTypes.object,
        flags: PropTypes.object,
    };

    static displayName = 'SearchResultsPage';

    constructor(props) {
        super(props);

        this.state = {
            screenWidth: window.innerWidth,
            openShowAvailableModal: false,
            infoModalType: '',
            isMobile: window.innerWidth < MOBILE_WIDTH,
            isListView: true,
            showAiSearch: false,
        };
        this.updateWindowDimensions = throttle(
            this.updateWindowDimensions.bind(this),
            100
        );
        this.showMapButtonRef = React.createRef();
    }

    componentDidMount() {
        document.body.classList.add('rec-no-overflow-y');
        this.updateWindowDimensions();
        window.addEventListener('resize', this.updateWindowDimensions);
        this.props.dispatch(actions.updateSearchCriteria('isMapExpanded', false));

        this.initializeAiSearch();

        const interval = setInterval(() => {
            if (document.querySelector('df-messenger')?.shadowRoot?.appendChild) {
                clearInterval(interval);
                const style = document.createElement('style');
                style.innerHTML = '.df-messenger-wrapper { bottom: 70px; }';
                style.id = 'search-page-style';
                document.querySelector('df-messenger').shadowRoot.appendChild(style);
            }
        }, 1000);

        this.refresh();
    }

    componentDidUpdate() {
        const { search } = this.props;
        if (search.isMapExpanded && this.state.isMobile) {
            this.props.dispatch(actions.updateSearchCriteria('isMapExpanded', false));
            showFullScreenMap(true);
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWindowDimensions);
        document.body.classList.remove('rec-no-overflow-y');
    }

    onApplyFilters = () => {
        setTimeout(() => {
            this.onEnteredDates();
            this.props.dispatch(actions.navigateToSearchResults());
        }, 200);
    };

    onEnteredDates() {
        const { search } = this.props;

        const isCampingSearch = this.isCampingSearch();
        if (search?.checkin_time && search?.checkout_time && !isCampingSearch) {
            const gaEvent = {
                category: 'Search',
                action: `${search.checkin_time} to ${search.checkout_time}`,
                label: 'Selected dates',
            };

            GaActions.trackRunSearch(gaEvent, () => {});
        }
    }

    initializeAiSearch = () => {
        const { search } = this.props;
        if (search?.ai_query) {
            this.setState({
                showAiSearch: true,
            });
        }
    };

    refresh = () => {
        const query = processQueryParams(this.props.router.searchParams);

        const { changed } = actions.parseQueryParams(
            query,
            this.props.dispatch,
            this.props
        );
        if (
            changed ||
            !this.props.searchResults.results ||
            this.props.searchResults.results.length === 0
        ) {
            this.props.dispatch(actions.fetchSearchResults());
        }
    };

    isCampingSearch = () => {
        const inventoryTypes = this.props?.search?.fg || [];
        const isCampingSearch = inventoryTypes.includes('camping') || false;
        return isCampingSearch;
    };

    updateWindowDimensions() {
        this.setState({
            screenWidth: window.innerWidth,
            isMobile: window.innerWidth < MOBILE_WIDTH,
        });
    }

    hasDatesInfoModal = (e) => {
        if (
            e.currentTarget.checked &&
            isEmpty(this.props.search.checkin_time) &&
            isEmpty(this.props.search.avail_months)
        ) {
            this.setState({
                infoModalType: e?.currentTarget?.name,
            });

            return true;
        }

        return false;
    };

    onShowAvailableModalChange = (openShowAvailableModal) => {
        this.setState({ openShowAvailableModal });
    };

    getDatesInfoModalMetadata = () => {
        const { infoModalType } = this.state;
        const metadata = {
            heading: '',
            text: '',
        };

        if (infoModalType === 'only_show_available') {
            metadata.heading = 'Only Show Available';
            metadata.text = 'Add Dates to Filter by Availability.';
        } else if (infoModalType === 'include_unavailable') {
            metadata.heading = 'Hide Unavailable';
            metadata.text = 'Add Dates to Filter by Unavailability.';
        } else if (infoModalType === 'include_notreservable') {
            metadata.heading = 'Hide First-come, First-served';
            metadata.text = 'Add Dates to Filter by First-come, First-served.';
        } else if (infoModalType === 'include_partially_available') {
            metadata.heading = 'Hide Partially Available';
            metadata.text = 'Add Dates to Filter by Availability.';
        }

        return metadata;
    };

    onOverlayToggleClick = () => {
        this.setState((prevState) => ({
            isListView: !prevState.isListView,
        }));
    };

    render() {
        const isCampingSearch = this.isCampingSearch();
        const { heading, text } = this.getDatesInfoModalMetadata();
        return (
            <div className="search-results">
                <div id="page-body">
                    <FlexRow hasGutters={false}>
                        <FlexCol>
                            {this.state.screenWidth >= 768 && (
                                <div
                                    role="toolbar"
                                    aria-label="Search Filters"
                                    className="header-filters-container header-primary"
                                >
                                    <SearchCriteriaInputs
                                        page={types.MAIN_SEARCH_RESULTS_PAGE}
                                        onApplyFilters={this.onApplyFilters}
                                        isCampingSearch={isCampingSearch}
                                        screenWidth={this.state.screenWidth}
                                        flags={this.props.flags}
                                        hasDatesInfoModal={this.hasDatesInfoModal}
                                        onShowAvailableModalChange={
                                            this.onShowAvailableModalChange
                                        }
                                    />
                                </div>
                            )}
                        </FlexCol>
                    </FlexRow>
                    {this.state.showAiSearch && !this.state.isMobile && (
                        <AiSearchInput onApplyFilters={this.onApplyFilters} />
                    )}

                    <MapControlProvider>
                        <FlexRow hasGutters={false}>
                            <SearchResultsList
                                screenWidth={this.state.screenWidth}
                                isCampingSearch={isCampingSearch}
                                hasDatesInfoModal={this.hasDatesInfoModal}
                                onShowAvailableModalChange={
                                    this.onShowAvailableModalChange
                                }
                                flags={this.props.flags}
                                showAiSearch={this.state.showAiSearch}
                            />
                            {!this.state.isMobile ? (
                                <div className="search-right-rail-wrap">
                                    <SearchResultsMapV2
                                        data={this.props.searchResults.results}
                                        isFetching={this.props.searchResults.loading}
                                    />
                                </div>
                            ) : (
                                <SearchResultsMapV2Modal
                                    isOpen={!this.state.isListView}
                                    onClose={() => this.onOverlayToggleClick()}
                                    triggerRef={this.showMapButtonRef}
                                >
                                    <SearchResultsMapV2
                                        data={this.props.searchResults.results}
                                        isFetching={this.props.searchResults.loading}
                                        isMobile
                                        closeMobileExpanded={() =>
                                            this.onOverlayToggleClick()
                                        }
                                    />
                                </SearchResultsMapV2Modal>
                            )}

                            {this.state.isMobile &&
                            this.state.isListView &&
                            !this.props.search.filtersVisible ? (
                                <SearchActionBarOverlay
                                    onToggle={this.onOverlayToggleClick}
                                    showMapButtonRef={this.showMapButtonRef}
                                />
                            ) : null}
                        </FlexRow>
                    </MapControlProvider>
                </div>

                <StyledModal
                    size="md"
                    isOpen={this.state.openShowAvailableModal}
                    heading={heading}
                    onRequestClose={() => this.onShowAvailableModalChange(false)}
                >
                    <Text>{text}</Text>
                    <ModalActions>
                        <Button onClick={() => this.onShowAvailableModalChange(false)}>
                            OK
                        </Button>
                    </ModalActions>
                </StyledModal>
            </div>
        );
    }
}

const mapStateToProps = (state) => {
    return {
        search: state.search,
        account: get(state, 'auth.account', null),
        tripPreferences: get(state, 'tripPreferences', null),
        searchResults: state.searchResults,
    };
};

export default withCompatRouter(
    withLDConsumer()(connect(mapStateToProps)(SearchResultsPage))
);

// cSpell:ignore checkin, ticketfacility, notreservable, timedentry, recarea, vehiclepermit, venuereservations, activitypass, treepermit, giftcard
